import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { Button } from "./ui/button";
import { loadFonts } from "./BrandingHead";
import { AnnouncementSketch } from "./AnnouncementSketch";

export const THEME_DEFAULTS = {
  sidebar_bg: "#0b1428", sidebar_text: "#C9AD6E", sidebar_font: "Manrope", border_color: "#C2A462", border_width: 1,
  panel_bg: "#0b0e14", page_bg: "#050505", title_color: "#ede9e1", title_size: 36, title_font: "Manrope",
  field_bg: "#1d2a46", field_border: "#4a4234", field_text: "#ede9e1",
};

const GROUPS = [
  ["Sidebar", [["sidebar_bg", "Background colour", "color"], ["sidebar_text", "Menu text colour", "color"], ["sidebar_font", "Menu font", "font"]]],
  ["Gold borders", [["border_color", "Border colour", "color"], ["border_width", "Border thickness (px)", "range", 0, 4]]],
  ["Panels & page", [["panel_bg", "Panel background", "color"], ["page_bg", "Page background", "color"]]],
  ["Section titles", [["title_size", "Title size (px)", "range", 20, 48], ["title_font", "Title font", "font"], ["title_color", "Title colour", "color"]]],
  ["Form fields", [["field_bg", "Field background", "color"], ["field_border", "Field border", "color"], ["field_text", "Field text", "color"]]],
];

function Control({ k, label, type, min, max, value, set, fonts }) {
  const tid = `theme-${k.replace(/_/g, "-")}`;
  if (type === "font") return (
    <label className="block text-xs text-muted-foreground">{label}
      <select value={value} onChange={(e) => set(k, e.target.value)} className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground" data-testid={tid}>
        {fonts.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
    </label>
  );
  if (type === "range") return (
    <label className="block text-xs text-muted-foreground">{label}: <span className="text-foreground">{value}</span>
      <input type="range" min={min} max={max} value={value} onChange={(e) => set(k, Number(e.target.value))} className="mt-2 w-full accent-[#C2A462]" data-testid={tid} />
    </label>
  );
  return (
    <label className="block text-xs text-muted-foreground">{label}
      <span className="mt-1 flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => set(k, e.target.value)} className="h-9 w-11 cursor-pointer rounded border border-input bg-transparent" data-testid={`${tid}-picker`} />
        <input value={value} onChange={(e) => set(k, e.target.value)} className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm font-mono text-foreground" data-testid={tid} />
      </span>
    </label>
  );
}

function Preview({ t }) {
  const field = { background: t.field_bg, borderColor: t.field_border, color: t.field_text };
  return (
    <div className="flex h-72 overflow-hidden rounded-md" style={{ background: t.page_bg, border: `1px solid ${t.border_color}55` }} data-testid="theme-preview">
      <div className="w-36 space-y-1 p-3" style={{ background: t.sidebar_bg, borderRight: `${t.border_width}px solid ${t.border_color}`, fontFamily: `'${t.sidebar_font}', sans-serif` }}>
        <div className="mb-3 text-center text-lg" style={{ color: t.border_color, fontFamily: "'Cormorant Garamond', serif" }}>BOS HQ</div>
        {["Dashboard", "Contacts", "Settings"].map((l, i) => <div key={l} className="rounded px-2 py-1.5 text-[12px]" style={{ color: t.sidebar_text, background: i === 0 ? `${t.border_color}22` : "transparent" }}>{l}</div>)}
      </div>
      <div className="flex-1 space-y-3 p-4">
        <div style={{ fontSize: Math.round(t.title_size * 0.6), fontFamily: `'${t.title_font}', sans-serif`, color: t.title_color }} className="font-bold leading-tight">Section title</div>
        <div className="space-y-2 rounded-md p-3" style={{ background: t.panel_bg, border: `${t.border_width}px solid ${t.border_color}73` }}>
          <div className="text-[11px] uppercase tracking-widest text-slate-400">Panel</div>
          <input readOnly value="Sample field" className="h-8 w-full rounded-md border px-2 text-sm" style={field} />
          <span className="inline-block rounded px-3 py-1 text-xs font-semibold" style={{ background: t.border_color, color: "#0b1428" }}>Button</span>
        </div>
      </div>
    </div>
  );
}

export function GlobalSettings() {
  const [data] = useGet("/public/theme");
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setT({ ...THEME_DEFAULTS, ...data.theme }); }, [data]);
  useEffect(() => { if (t) loadFonts([t.sidebar_font, t.title_font]); }, [t]);
  if (!t) return null;
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  const save = async (body) => {
    setBusy(true);
    try {
      const { data: r } = await api.put("/agency/theme", body);
      setT({ ...THEME_DEFAULTS, ...r.theme });
      window.dispatchEvent(new Event("theme-updated"));
      toast.success(Object.keys(body).length ? "Global settings saved for all of BOS HQ" : "Reset to BOS HQ default");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const changed = Object.fromEntries(Object.entries(t).filter(([k, v]) => String(v).toLowerCase() !== String(THEME_DEFAULTS[k]).toLowerCase()));
  return (
    <div className="space-y-6" data-testid="global-settings">
      <div className="grid gap-6 xl:grid-cols-[1fr_440px]">
        <div className="grid gap-4 sm:grid-cols-2">
          {GROUPS.map(([g, ctrls]) => (
            <div key={g} className="space-y-3 rounded-md border bg-card p-4" data-testid={`theme-group-${g.split(" ")[0].toLowerCase()}`}>
              <div className="overline">{g}</div>
              {ctrls.map(([k, label, type, min, max]) => <Control key={k} k={k} label={label} type={type} min={min} max={max} value={t[k]} set={set} fonts={data.fonts} />)}
            </div>
          ))}
        </div>
        <div className="space-y-3 xl:sticky xl:top-6 self-start">
          <div className="overline">Live preview</div>
          <Preview t={t} />
          <div className="flex gap-2">
            <Button onClick={() => save(changed)} disabled={busy} data-testid="theme-save-btn">Save for all of BOS HQ</Button>
            <Button variant="secondary" onClick={() => { if (window.confirm("Reset every colour, font and size to the BOS HQ default?")) save({}); }} disabled={busy} data-testid="theme-reset-btn"><RotateCcw className="h-4 w-4 mr-1" />Reset to BOS HQ default</Button>
          </div>
          <p className="text-xs text-muted-foreground">These settings apply to every entity. Per-entity styling can be offered later as a plan feature.</p>
        </div>
      </div>
      <AnnouncementSketch />
    </div>
  );
}
